// Erros do cliente: ProblemDetails (RFC 9457) estendido pela plataforma → GiyusApiError.
import { HEADERS, PROBLEM_JSON } from './headers';

export interface FieldError {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

export interface BlockingConstraint {
  readonly code: string;
  readonly resourceType?: string;
  readonly resourceId?: string;
  readonly detail?: string;
  /** Campo extra do motivo (delegation log, GIY-071): tipo de certificação, tarefa ou conselho em falta. */
  readonly certificationTypeCode?: string;
  readonly taskCode?: string;
  readonly councilCode?: string;
}

/** `application/problem+json` conforme Parte 2.6.8 / 13.1. */
export interface GiyusProblemDetails {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly errorCode?: string;
  readonly correlationId?: string;
  readonly errors?: readonly FieldError[];
  readonly blockingConstraints?: readonly BlockingConstraint[];
  readonly [extension: string]: unknown;
}

export class GiyusApiError extends Error {
  override readonly name = 'GiyusApiError';
  readonly status: number;
  readonly errorCode: string;
  readonly correlationId: string | undefined;
  readonly problem: GiyusProblemDetails;
  readonly fieldErrors: readonly FieldError[];
  readonly blockingConstraints: readonly BlockingConstraint[];
  readonly retryAfterSeconds: number | undefined;

  constructor(problem: GiyusProblemDetails, status: number, correlationId?: string) {
    super(problem.title ?? `HTTP ${String(status)}`);
    this.status = status;
    this.errorCode = problem.errorCode ?? fallbackErrorCode(status);
    this.correlationId = problem.correlationId ?? correlationId;
    this.problem = problem;
    this.fieldErrors = problem.errors ?? [];
    this.blockingConstraints = problem.blockingConstraints ?? [];
    this.retryAfterSeconds = undefined;
  }

  /** 423: trava de compliance (Parte 0.3, regra 6). */
  get isLocked(): boolean {
    return this.status === 423;
  }

  /** 404 fora do escopo ou 403 PLT_NETWORK_SCOPE_REQUIRED → mesma página NotFound (Parte 14.5). */
  get isOutOfScope(): boolean {
    return this.status === 404 || this.errorCode === 'PLT_NETWORK_SCOPE_REQUIRED';
  }

  get isValidation(): boolean {
    return this.status === 422 && this.fieldErrors.length > 0;
  }

  static withRetryAfter(
    error: GiyusApiError,
    retryAfterSeconds: number | undefined,
  ): GiyusApiError {
    return Object.assign(error, { retryAfterSeconds });
  }
}

/** Falha de rede (sem resposta HTTP). */
export class GiyusNetworkError extends Error {
  override readonly name = 'GiyusNetworkError';
  readonly correlationId: string | undefined;

  constructor(cause: unknown, correlationId?: string) {
    super('network');
    this.cause = cause;
    this.correlationId = correlationId;
  }
}

export function fallbackErrorCode(status: number): string {
  switch (status) {
    case 400:
      return 'PLT_BAD_REQUEST';
    case 401:
      return 'PLT_UNAUTHORIZED';
    case 403:
      return 'PLT_FORBIDDEN';
    case 404:
      return 'PLT_NOT_FOUND';
    case 409:
      return 'PLT_CONFLICT';
    case 412:
      return 'PLT_PRECONDITION_FAILED';
    case 422:
      return 'PLT_VALIDATION';
    case 423:
      return 'PLT_LOCKED';
    case 429:
      return 'PLT_RATE_LIMITED';
    default:
      return 'PLT_UNEXPECTED';
  }
}

export function isProblemJson(response: Response): boolean {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.toLowerCase().includes(PROBLEM_JSON);
}

/** Converte uma resposta de erro em `GiyusApiError` (lê o corpo problem+json quando existir). */
export async function toGiyusApiError(response: Response): Promise<GiyusApiError> {
  const correlationId = response.headers.get(HEADERS.correlationId) ?? undefined;
  let problem: GiyusProblemDetails = { status: response.status };
  if (isProblemJson(response)) {
    try {
      problem = (await response.clone().json()) as GiyusProblemDetails;
    } catch {
      problem = { status: response.status };
    }
  }
  const error = new GiyusApiError(problem, response.status, correlationId);
  const retryAfter = response.headers.get(HEADERS.retryAfter);
  const seconds = retryAfter ? Number.parseInt(retryAfter, 10) : Number.NaN;
  return GiyusApiError.withRetryAfter(error, Number.isNaN(seconds) ? undefined : seconds);
}
